export type VarInputNodeSnapshot = {
  id: string;
  data: {
    text?: string;
    label?: string;
  };
};

const DOCUMENTATION_BLOCK = {
  description: 'Structure of input data for the agent',
  fields: {
    inputId: 'Unique identifier of the input node (required)',
    inputText: 'Text or content that will be processed by the agent (required)',
    links: 'Array of links related to the input (optional)',
    files: 'Array of files attached to the input (optional)',
    isExtraction:
      'Boolean: indicates if this input is for data extraction (optional, options: true, false)',
    typeOCR:
      "String: type of OCR applied to the input (optional, default: 'mistral_ocr', options: 'mistral_ocr', 'mistral_vision', 'google_ocr', 'google_ocr_form_parse')",
  },
  nestedFields: {
    links: {
      name: 'Descriptive name of the link (optional)',
      url: 'Complete URL of the link (optional)',
    },
    files: {
      name: 'File name with extension (optional)',
      base64: 'File content encoded in base64 (optional)',
    },
  },
} as const;

export type VariableInputsConfigResult = string | 'No var input nodes found';

export function buildVariableInputsConfigJson(
  varInputNodes: VarInputNodeSnapshot[],
): VariableInputsConfigResult {
  if (!varInputNodes.length) {
    return 'No var input nodes found';
  }

  return JSON.stringify(
    {
      _documentation: DOCUMENTATION_BLOCK,
      variableInputs: varInputNodes.map(node => ({
        inputId: node.id,
        inputText: node.data.text || '',
      })),
    },
    null,
    2,
  );
}

export function removeDocumentationFromConfigJson(jsonString: string): string {
  try {
    const parsed = JSON.parse(jsonString) as Record<string, unknown>;
    const filtered = Object.fromEntries(
      Object.entries(parsed).filter(([key]) => key !== '_documentation'),
    );
    return JSON.stringify(filtered, null, 2);
  } catch {
    return 'Invalid JSON';
  }
}

export function toConfigDisplayString(
  config: VariableInputsConfigResult,
  hideDocumentation: boolean,
): string {
  if (config === 'No var input nodes found') {
    return config;
  }
  if (!hideDocumentation) {
    return config;
  }
  return removeDocumentationFromConfigJson(config);
}

export function collectVarInputSnapshotsFromNodeInternals(
  nodeInternals: Map<
    string,
    { id: string; type?: string; data?: { text?: string; label?: string } }
  >,
): VarInputNodeSnapshot[] {
  const nodesArray = Array.from(nodeInternals.values());
  return nodesArray
    .filter(node => node.type === 'varInputNode')
    .map(node => ({
      id: node.id,
      data: {
        text: node.data?.text,
        label: node.data?.label,
      },
    }));
}
